import { SectionCard } from '@/components/SectionCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { ItemSummaryStats } from '@/features/items/components/ItemSummaryStats'
import { useConfirm } from '@/hooks/useConfirm'
import { useDebounce } from '@/hooks/useDebounce'
import { getMutationOptions } from '@/utils/mutation'
import { Edit2, Monitor, Plus, Search, Settings, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDeleteProduct, useGetProducts } from '../hooks/useProduct'
import { type Product } from '../types/product'
import { ProductCategoryDialog } from './ProductCategoryDialog'
import { ProductDialog } from './ProductDialog'

export function EquipmentView() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const [search, setSearch] = useState('')
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const debouncedSearch = useDebounce(search, 500)
  const { data: products = [], isLoading: loadingProduct } = useGetProducts({
    categoryId: selectedCategoryId ?? undefined,
    search: debouncedSearch,
  })
  const deleteProduct = useDeleteProduct()
  const { isAdmin } = useAuth()

  const navigate = useNavigate()

  const [productId, setProductId] = useState<string | null>(null)
  const [productOpen, setProductOpen] = useState(false)
  const [productCategoriesOpen, setProductCategoriesOpen] = useState(false)

  const canManage = isAdmin

  const uniqueCategories = [...new Map(products.map(p => [p.categoryId, p.categoryName])).entries()]

  const categoryMap = useMemo(() => {
    return new Map(products.map(p => [p.categoryId, p.categoryName]))
  }, [products])

  const selectedCategoryName = selectedCategoryId ? categoryMap.get(selectedCategoryId) : null

  function toggleCategory(id: string) {
    setSelectedCategoryId(prev => (prev === id ? null : id))
  }

  function getSummary(products: Product[]) {
    return products.reduce(
      (acc, product) => {
        const items = product?.itemSummary

        acc.available += items?.available
        acc.all += items?.total
        return acc
      },
      { available: 0, all: 0 },
    )
  }

  const categorySummary = useMemo(() => getSummary(products), [products])

  const handleDeleteProduct = async (productId: string) => {
    const product = products.find(i => i.id === productId)
    const detail = `${product?.name ?? ''} ${product?.brand} ${product?.model}`
    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('equipment.model.confirmDeleteDescription', { detail }),
    })

    if (confirmed) {
      const options = getMutationOptions({
        successMessage: t('equipment.model.toast.deleteSuccess'),
      })
      deleteProduct.mutate(productId, options)
    }
  }

  return (
    <div data-testid="equipment-list">
      <SectionCard
        title={`${t('equipment.model.title')} (${products.length})`}
        right={
          <>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="equipment-search-input"
                placeholder={t('common.placeholder.searchEquipment')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 w-full sm:w-[200px] bg-secondary/50 pl-8 text-xs"
              />
            </div>
            {canManage && (
              <>
                <Button
                  data-testid="equipment-create-button"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    setProductId(null)
                    setProductOpen(true)
                  }}
                >
                  <Plus className="mr-1 size-3" />
                  {t('equipment.model.add')}
                </Button>
                <Button
                  data-testid="equipment-category-settings-button"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setProductCategoriesOpen(true)}
                >
                  <Settings className="mr-1 size-3" />
                  {t('equipment.category.title')}
                </Button>
              </>
            )}
          </>
        }
        subtitle={
          <>
            {uniqueCategories.map(([id, name]) => (
              <Badge
                key={id}
                data-testid="equipment-category-badge"
                variant={selectedCategoryId === id ? 'default' : 'outline'}
                className="cursor-pointer text-[12px]"
                onClick={() => toggleCategory(id)}
              >
                {name}
              </Badge>
            ))}
            <Button
              data-testid="equipment-reset-category-button"
              variant="outline"
              size="sm"
              className="h-7 text-[12px]"
              onClick={() => setSelectedCategoryId(null)}
            >
              {t('common.reset')}
            </Button>
          </>
        }
      >
        <>
          {selectedCategoryId && (
            <div className="pb-2">
              <span className="text-sm font-medium text-foreground">
                {loadingProduct ? (
                  t('common.loading')
                ) : (
                  <>
                    {selectedCategoryName} {categorySummary.available}/{categorySummary.all}
                  </>
                )}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {products.map(product => {
              return (
                <SectionCard
                  key={product.id}
                  data-testid="equipment-card"
                  data-product-id={product.id}
                  onClick={() => navigate(`/equipment/${product.id}/items`)}
                  cardClass="cursor-pointer border-border/50 bg-secondary/20 transition-all hover:border-primary/30"
                  headerClass="pb-0"
                  header={
                    <div className="flex flex-col gap-0.5 min-h-[40px] min-w-0">
                      {product.name ? (
                        <>
                          <span
                            data-testid="equipment-card-name"
                            className="text-sm font-semibold text-foreground break-all line-clamp-2"
                          >
                            {product.name}
                          </span>
                          <span
                            data-testid="equipment-card-brand-model"
                            className="text-xs text-muted-foreground break-all line-clamp-1"
                          >
                            {product.brand} {product.model}
                          </span>
                        </>
                      ) : (
                        <span data-testid="equipment-card-name" className="text-sm font-semibold text-foreground">
                          {product.brand} {product.model}
                        </span>
                      )}
                    </div>
                  }
                  right={
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-1">
                        {canManage && (
                          <Button
                            data-testid="equipment-card-edit"
                            variant={'outline'}
                            size="sm"
                            className="h-6"
                            onClick={e => {
                              e.stopPropagation()
                              setProductId(product.id)
                              setProductOpen(true)
                            }}
                          >
                            <Edit2 className="size-3" />
                          </Button>
                        )}

                        <Badge
                          data-testid="equipment-card-total"
                          variant="outline"
                          className="text-[10px] text-muted-foreground"
                        >
                          <Monitor className="mr-1 size-3" />
                          {product?.itemSummary?.total}
                        </Badge>
                      </div>

                      {canManage && (
                        <div className="flex justify-end">
                          <Button
                            data-testid="equipment-card-delete"
                            variant={'outline'}
                            size="sm"
                            className="h-6 border-destructive/30 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={e => {
                              e.stopPropagation()
                              handleDeleteProduct(product.id)
                            }}
                          >
                            <Trash2 className="size-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  }
                >
                  <Badge
                    data-testid="equipment-card-category"
                    variant="outline"
                    className="w-fit text-[10px] text-muted-foreground"
                  >
                    {product.categoryName}
                  </Badge>
                  <ItemSummaryStats summary={product?.itemSummary}></ItemSummaryStats>
                </SectionCard>
              )
            })}
          </div>
        </>
      </SectionCard>
      <ProductDialog open={productOpen} onOpenChange={setProductOpen} productId={productId} />
      <ProductCategoryDialog
        open={productCategoriesOpen}
        onOpenChange={setProductCategoriesOpen}
      ></ProductCategoryDialog>
    </div>
  )
}
